import { useState } from 'react';
import type { ExpenseItemWithRelations } from '@peles/shared';
import { EntityModal } from '../common/EntityModal';
import { SearchableListSelect } from '../common/SearchableListSelect';
import { useDeleteExpenseItem, useExpenseItems } from '../../hooks/useExpenses';
import { extractApiErrorMessage } from '../../utils/apiError';
import { lineName } from '../planning/itemsGridModel';

interface DeleteLineWithItemsModalProps {
  /** The line being deleted, and how many items the server said it has. */
  line: ExpenseItemWithRelations | null;
  itemCount: number;
  onClose: () => void;
}

/**
 * A line with items is deleted only once its items have somewhere to be:
 * moved to another line of the same event, or deleted with it (their tasks
 * stay on the Tasks list). Nothing is orphaned.
 */
export function DeleteLineWithItemsModal(props: DeleteLineWithItemsModalProps) {
  // The event's lines are read only while there is a line to delete.
  return props.line ? <DeleteLineDialog {...props} line={props.line} /> : null;
}

function DeleteLineDialog({
  line,
  itemCount,
  onClose,
}: DeleteLineWithItemsModalProps & { line: ExpenseItemWithRelations }) {
  const { data: lines = [] } = useExpenseItems({ eventId: line.eventId });
  const remove = useDeleteExpenseItem();
  const [choice, setChoice] = useState<'move' | 'delete'>('move');
  const [target, setTarget] = useState<string | null>(null);
  const [error, setError] = useState('');
  const others = lines.filter((l) => l.id !== line.id);
  const plural = itemCount === 1 ? 'item' : 'items';

  async function submit() {
    setError('');
    try {
      await remove.mutateAsync({
        id: line.id,
        items: choice === 'move' ? { moveItemsTo: target! } : { deleteItems: true },
      });
      onClose();
    } catch (err) {
      setError(extractApiErrorMessage(err, 'The line could not be deleted'));
    }
  }

  return (
    <EntityModal
      open
      onClose={onClose}
      title="This line has items"
      size="sm"
      footer={
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={remove.isPending || (choice === 'move' && !target)}
            className="px-4 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
          >
            {choice === 'move' ? 'Move and delete' : `Delete with its ${plural}`}
          </button>
        </div>
      }
    >
      {
        <div className="space-y-3 text-sm">
          <p className="text-gray-700">
            “{lineName(line)}” has {itemCount} {plural}. Where should{' '}
            {itemCount === 1 ? 'it' : 'they'} go?
          </p>
          <label className="flex items-start gap-2 min-h-11 sm:min-h-0">
            <input
              type="radio"
              name="line-items"
              checked={choice === 'move'}
              onChange={() => setChoice('move')}
              className="mt-1"
            />
            <span className="flex-1">
              Move them to another need
              {choice === 'move' && (
                <span className="block mt-2">
                  <SearchableListSelect
                    items={others}
                    value={target}
                    onChange={(v) => setTarget(typeof v === 'string' && v ? v : null)}
                    getValue={(l) => l.id}
                    getLabel={(l) => lineName(l)}
                    placeholder="Choose a need…"
                    ariaLabel="Move the items to"
                    emptyMessage="This event has no other need"
                  />
                </span>
              )}
            </span>
          </label>
          <label className="flex items-start gap-2 min-h-11 sm:min-h-0">
            <input
              type="radio"
              name="line-items"
              checked={choice === 'delete'}
              onChange={() => setChoice('delete')}
              className="mt-1"
            />
            <span>Delete them with it — their tasks stay on the Tasks list</span>
          </label>
          {error && <p className="text-red-600">{error}</p>}
        </div>
      }
    </EntityModal>
  );
}
